import { useMemo, useState } from 'react';
import { Autocomplete, Chip, Stack, SxProps, TextField } from '@mui/material';

import { BranchBadge } from './BranchBadge';
import { GameElementIcon } from './GameElementIcon';

import {
    GameElementDto,
    preferMainById,
    useGameElements,
    useGameElementsIncludingBranches,
} from '../../hooks/gameElements';

const ALL_MATCH_FN = () => true;

interface GameElementPickerProps<Multiple extends boolean = false> {
    value?: Multiple extends true ? Array<GameElementDto> : GameElementDto;
    onChange?: (
        id: Multiple extends true
            ? Array<GameElementDto>
            : GameElementDto | null,
    ) => void;
    multiple?: Multiple;
    size?: 'small' | 'medium';
    disableClearable?: boolean;
    label?: string;
    recommendedGameElementIds?: string[];
    gameElementFilter?: (gameElement: GameElementDto) => boolean;
    sx?: SxProps;
    /** Show feature-branch elements too, each marked with a warning badge. */
    includeBranches?: boolean;
    /**
     * Optional notification callback fired whenever the search text inside
     * the autocomplete changes. The picker keeps full ownership of the
     * input state — this is purely observational, so a parent can react
     * to what the user is typing (e.g. enable a "Create new" action when
     * the query doesn't match any existing option) without interfering
     * with MUI Autocomplete's controlled-input lifecycle.
     */
    onInputChange?: (value: string) => void;
}

export const GameElementPicker = <Multiple extends boolean = false>(
    props: GameElementPickerProps<Multiple>,
) => {
    const {
        value: items,
        onChange,
        multiple,
        size,
        disableClearable,
        label,
        gameElementFilter = ALL_MATCH_FN,
        sx,
        includeBranches = false,
        onInputChange: onInputChangeNotify,
    } = props;
    const mainQuery = useGameElements({ enabled: !includeBranches });
    const branchQuery = useGameElementsIncludingBranches({
        enabled: includeBranches,
    });
    const gameElementsQuery = includeBranches ? branchQuery : mainQuery;
    const [searchValue, setSearchValueInternal] = useState('');

    const setSearchValue = (value: string) => {
        setSearchValueInternal(value);
        onInputChangeNotify?.(value);
    };

    const options = useMemo(() => {
        if (!gameElementsQuery.data) {
            return [];
        }

        const all =
            gameElementsQuery.data.gameElements.filter(gameElementFilter);

        // Without the flag the backend already returns only main rows.
        if (!includeBranches) {
            return all;
        }

        return [...preferMainById(all).values()];
    }, [gameElementsQuery.data, gameElementFilter, includeBranches]);

    return (
        <Autocomplete
            sx={sx}
            disableClearable={disableClearable}
            size={size}
            multiple={multiple}
            loading={gameElementsQuery.isPending}
            // Keep the typed search text visible after the field loses
            // focus. By default MUI Autocomplete (in non-freeSolo mode)
            // clears the input on blur whenever the typed value doesn't
            // match a selected option — that breaks the "request new
            // game element" flow because the user clicks into the Jira
            // summary field next and the input would visually empty out
            // even though our parent state still holds the value.
            clearOnBlur={false}
            options={
                searchValue.length >= 2 || options.length < 100 ? options : []
            }
            noOptionsText={'Type to search'}
            onChange={(_, value) => onChange?.(value)}
            onInputChange={(_, nextSearchValue) =>
                setSearchValue(nextSearchValue)
            }
            getOptionKey={(option) => `${option.id}:${option.branch}`}
            getOptionLabel={(option) => formatGameElementName(option)}
            renderOption={(props, option, _state, ownerState) => {
                const { key, ...other } = props;
                return (
                    <li {...other} key={key}>
                        <Stack
                            direction="row"
                            spacing={1}
                            sx={{ alignItems: 'center' }}
                        >
                            <GameElementIcon gameElementId={option.id} />
                            <p>{ownerState.getOptionLabel(option)}</p>
                            <BranchBadge element={option} />
                        </Stack>
                    </li>
                );
            }}
            renderValue={(value, getItemProps) => {
                // `Multiple` is generic here, so TS can't resolve MUI's conditional value/getItemProps types.
                if (multiple) {
                    const arr = value as unknown as GameElementDto[];
                    return arr.map((option, index) => {
                        const { key, ...tagProps } = (
                            getItemProps as unknown as (a: {
                                index: number;
                            }) => { key: number } & Record<string, unknown>
                        )({ index });
                        return (
                            <Chip
                                {...tagProps}
                                key={key ?? option.id}
                                variant="outlined"
                                size={size ?? 'small'}
                                sx={{
                                    '& .MuiChip-label': {
                                        overflow: 'visible',
                                    },
                                }}
                                label={
                                    <Stack
                                        direction="row"
                                        spacing={0.75}
                                        sx={{ alignItems: 'center' }}
                                    >
                                        <span>
                                            {formatGameElementName(option)}
                                        </span>
                                        <BranchBadge element={option} />
                                    </Stack>
                                }
                            />
                        );
                    });
                }
                // Single-select: MUI renders selected value as text; return
                // name + badge so the branch indicator is visible inline.
                if (!value || Array.isArray(value)) return null;
                const selected = value as GameElementDto;
                return (
                    <Stack
                        direction="row"
                        spacing={0.5}
                        sx={{ alignItems: 'center' }}
                    >
                        <span>{formatGameElementName(selected)}</span>
                        <BranchBadge element={selected} />
                    </Stack>
                );
            }}
            renderInput={(params) => <TextField label={label} {...params} />}
            value={items}
            isOptionEqualToValue={(option, value) => option.id === value.id}
        />
    );
};

const formatGameElementName = (gameElement: GameElementDto) => {
    const lastSlashIndex = gameElement.name.lastIndexOf('/');
    const suffixIndex = gameElement.name.indexOf('.prefab');
    return gameElement.name.slice(lastSlashIndex + 1, suffixIndex);
};
